import {
  BillGroupStatusEnum,
  IBillGroupResp,
  MCustomSelectProps,
} from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { useEffect, useMemo, useRef } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useGetListData } from '../../../api/queryUtils';
import { BILL_GROUP_STATUS_DISPLAY } from '../../../constants/billGroups';
import { MFlex } from '../chakra';
import { MainSearchInputV2 } from '../MCustomSelect/components/MainSearchInputV2';
import { MCustomSelect } from '../MCustomSelect/MCustomSelect';
import { Highlighter } from '../MPageSearchInput/Highlighter';
import MText from '../MText';

interface BillGroupDropdownProps {
  accountId: string;
  statusFilter?: BillGroupStatusEnum[];
  field: ControllerRenderProps<any, any>;
  customSelectProps?: MCustomSelectProps;
  onChangeBillGroup?: (billGroup: IBillGroupResp | undefined) => void;
}

const PAGER: TDataTablePager<keyof IBillGroupResp> = {
  ...DEFAULT_PAGER,
  sortField: 'name',
};

const EMPTY_LIST: IBillGroupResp[] = [];

/** Fits an id plus the longest status label with a comfortable gap between. */
const ITEM_MIN_WIDTH = '14rem';

export const BillGroupDropdown = ({
  accountId,
  statusFilter,
  field,
  customSelectProps,
  onChangeBillGroup,
}: BillGroupDropdownProps) => {
  const onChangeBillGroupRef = useRef(onChangeBillGroup);
  onChangeBillGroupRef.current = onChangeBillGroup;

  const filters = useMemo(() => ({ accountId }), [accountId]);

  const { data: billGroups, isLoading } = useGetListData<
    IBillGroupResp,
    IBillGroupResp[]
  >(
    'billGroups',
    { config: PAGER, filters },
    {
      isGetAll: true,
      endpointArgs: { accountId },
      select: (data) =>
        data.content.filter(
          (billGroup) =>
            !statusFilter || statusFilter.includes(billGroup.status),
        ),
    },
  );

  useEffect(() => {
    if (!billGroups) {
      return;
    }
    if (billGroups.length === 1) {
      field.onChange(billGroups[0].id);
      onChangeBillGroupRef.current?.(billGroups[0]);
    } else if (field.value) {
      onChangeBillGroupRef.current?.(
        billGroups.find(({ id }) => id === field.value),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billGroups]);

  const handleChange = (value: string) => {
    field.onChange(value);
    onChangeBillGroupRef.current?.(billGroups?.find(({ id }) => id === value));
  };

  return (
    <MCustomSelect
      placeholder="Select a Bill Group"
      useMainInputAsSearchInput
      MainInputComponent={MainSearchInputV2}
      useHighlightedItemList
      hideHighlightSearchResultsCount
      items={billGroups || EMPTY_LIST}
      itemTitle="name"
      itemValue="id"
      loading={isLoading}
      renderItemContent={BillGroupItemRenderer}
      {...field}
      {...customSelectProps}
      onChange={handleChange}
    />
  );
};

export const BillGroupItemRenderer = ({
  item,
  query,
}: {
  item: IBillGroupResp;
  query: string;
}) => {
  return (
    <MFlex
      direction="column"
      overflow="hidden"
      whiteSpace="nowrap"
      textOverflow="ellipsis"
      w="100%"
      // The id and status sit at opposite ends of the second row, so a dropdown
      // sized to its content (short bill group names) would push them into each
      // other. This floor keeps them legibly apart; wider dropdowns are
      // unaffected since it only ever raises a narrower width.
      minW={ITEM_MIN_WIDTH}
    >
      <MFlex justifyContent="space-between" w="100%">
        <Highlighter
          title={item.name}
          color="tPurple.dark"
          fontSize="sm"
          fontWeight="medium"
          textToHighlight={item.name}
          searchWords={[query]}
          whiteSpace="normal"
        />
      </MFlex>
      <MFlex justifyContent="space-between" w="100%" gap={4}>
        <MText color="tGray.darkPurple" fontSize="xs" truncate>
          {item.id}
        </MText>
        <MText color="tGray.darkPurple" fontSize="xs" flexShrink={0}>
          {BILL_GROUP_STATUS_DISPLAY[item.status]}
        </MText>
      </MFlex>
    </MFlex>
  );
};
